'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { usePathname } from 'next/navigation';

export default function GarlicBookLayout({
    children,
}: {
    children: React.ReactNode;
}) {
    const pathname = usePathname();

    const chapters = [
        { title: 'Introduction', path: '/library/books/garlic-recipes/introduction', type: 'link' },
        { title: 'Appetizers', type: 'header' },
        { title: 'Garlic Yogurt Cheese', path: '/library/books/garlic-recipes/garlic-yogurt-cheese', type: 'link' },
        { title: 'Garlic Toast with Olive Tapenade', path: '/library/books/garlic-recipes/garlic-toast-tapenade', type: 'link' },
        { title: 'Roasted Garlic with Herbs & French Bread', path: '/library/books/garlic-recipes/roasted-garlic-herbs', type: 'link' },
        { title: 'Eggplant Pizzas with Garlic, Tomato & Cheese', path: '/library/books/garlic-recipes/eggplant-pizzas', type: 'link' },
        { title: 'Mushroom Filo Tartlets with Garlic & Thyme', path: '/library/books/garlic-recipes/mushroom-filo-tartlets', type: 'link' },
        { title: 'Fennel-Roasted Peppers with Garlic & Parsley', path: '/library/books/garlic-recipes/fennel-roasted-peppers', type: 'link' },
        { title: 'Garlic & Sorrel Soup with Semolina', path: '/library/books/garlic-recipes/garlic-sorrel-soup', type: 'link' },
        { title: 'Salads', type: 'header' },
        { title: 'Mixed Vegetable Salad with Garlic Hollandaise', path: '/library/books/garlic-recipes/mixed-vegetable-salad', type: 'link' },
        { title: 'Egg-Safe Caesar Salad', path: '/library/books/garlic-recipes/egg-safe-caesar-salad', type: 'link' },
        { title: 'Roasted Tomato Salad with Garlic, Basil & Parmesan', path: '/library/books/garlic-recipes/roasted-tomato-salad', type: 'link' },
        { title: 'Broiled Pepper Salad with Garlic, Basil & Sun-Dried Tomatoes', path: '/library/books/garlic-recipes/broiled-pepper-salad', type: 'link' },
        { title: 'Baby Potato & Lima Bean Salad with Lemon Chive Dressing', path: '/library/books/garlic-recipes/baby-potato-lima-bean-salad', type: 'link' },
        { title: 'Chévre Salad with Pear, Walnuts & Garlic Walnut Dressing', path: '/library/books/garlic-recipes/chevre-salad', type: 'link' },
        { title: 'Vegetarian Dishes', type: 'header' },
        { title: 'Spicy Couscous with Garlic, Mango Chutney & Apples', path: '/library/books/garlic-recipes/spicy-couscous', type: 'link' },
        { title: 'Pesto, Tomato & Zucchini Flan', path: '/library/books/garlic-recipes/pesto-tomato-zucchini-flan', type: 'link' },
        { title: 'Roasted Winter Vegetables with Garlic Paste', path: '/library/books/garlic-recipes/roasted-winter-vegetables', type: 'link' },
        { title: 'Herby Potato Pie with Garlic & Cream', path: '/library/books/garlic-recipes/herby-potato-pie', type: 'link' },
        { title: 'Green Risotto with Parmesan & Spring Vegetables', path: '/library/books/garlic-recipes/green-risotto', type: 'link' },
        { title: 'Gorgonzola Pizza with Garlic, Olives & Oregano', path: '/library/books/garlic-recipes/gorgonzola-pizza', type: 'link' },
        { title: 'Meat and Poultry', type: 'header' },
        { title: 'Chorizo & Cherry Tomato Skillet with Bacon, Garlic & Olives', path: '/library/books/garlic-recipes/chorizo-cherry-tomato-skillet', type: 'link' },
        { title: 'Red-Hot Ribs with Spicy Garlic-Chili Glaze', path: '/library/books/garlic-recipes/red-hot-ribs', type: 'link' },
        { title: 'Bacon & Walnut Penne with Cream Cheese & Thyme', path: '/library/books/garlic-recipes/bacon-walnut-penne', type: 'link' },
        { title: 'Mini Lamb Meatball Pita Pockets with Tzatziki', path: '/library/books/garlic-recipes/mini-lamb-meatball-pita-pockets', type: 'link' },
        { title: 'Mediterranean Steak Broil with Marinated Vegetables & Rustic Bread', path: '/library/books/garlic-recipes/mediterranean-steak-broil', type: 'link' },
        { title: 'Honey & Mustard Turkey with Basil & Garlic', path: '/library/books/garlic-recipes/honey-mustard-turkey', type: 'link' },
        { title: 'Spicy Chicken Kebabs with Toasted Almond Marinade & Mango Chutney', path: '/library/books/garlic-recipes/spicy-chicken-kebabs', type: 'link' },
        { title: 'Roast Chicken with Garlic, Bacon & Tarragon', path: '/library/books/garlic-recipes/roast-chicken', type: 'link' },
        { title: 'Crunchy Tomato-Coated Chicken with Gratin Dauphinois', path: '/library/books/garlic-recipes/crunchy-tomato-chicken', type: 'link' },
        { title: 'Crispy Garlic Chicken with Buttered Baby Vegetables', path: '/library/books/garlic-recipes/crispy-garlic-chicken', type: 'link' },
        { title: 'Fish & Seafood', type: 'header' },
        { title: 'Spiced Coconut Mussels with Lemongrass & Ginger', path: '/library/books/garlic-recipes/spiced-coconut-mussels', type: 'link' },
        { title: 'Stuffed Baked Sea Bass with Garlic, Herbs & Lemon', path: '/library/books/garlic-recipes/stuffed-baked-sea-bass', type: 'link' },
        { title: 'Tuna Steaks with Lime & Herb Sauce', path: '/library/books/garlic-recipes/tuna-steaks', type: 'link' },
        { title: 'Jumbo Shrimp Sautéed in Garlic & Brandy', path: '/library/books/garlic-recipes/jumbo-shrimp', type: 'link' },
        { title: 'Monkfish with Tomato, Pepper & Basil Sauce', path: '/library/books/garlic-recipes/monkfish', type: 'link' },
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* Book Header / Navigation Bar */}
            <div className="bg-card border-b border-border sticky top-16 z-30">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                    <Link
                        href="/library"
                        className="flex items-center text-muted-foreground hover:text-primary transition-colors"
                    >
                        <Icon name="ArrowLeftIcon" size={20} className="mr-2" />
                        <span className="font-medium">Back to Library</span>
                    </Link>
                    <div className="font-heading font-semibold text-foreground hidden md:block">
                        Healthy & Delicious Garlic Recipes You’ll Love
                    </div>
                    <div className="w-24"></div> {/* Spacer for centering */}
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Main Content Area (Left) */}
                    <main className="flex-1 min-w-0">
                        <div className="bg-card rounded-xl border border-border p-8 md:p-12 shadow-sm prose prose-lg dark:prose-invert max-w-none">
                            {children}

                            {/* Contribution CTA */}
                            <div className="mt-12 pt-8 border-t border-border not-prose">
                                <div className="bg-muted/50 rounded-lg p-6 text-center border border-border/50">
                                    <Icon name="PaperAirplaneIcon" size={32} className="mx-auto text-primary mb-3" />
                                    <h3 className="font-heading font-semibold text-lg mb-2 text-foreground">Tried this recipe?</h3>
                                    <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
                                        Share your photos, variations, or tips with the community! We'd love to feature your contribution.
                                    </p>
                                    <Link
                                        href="/contact"
                                        className="inline-flex items-center px-4 py-2 bg-primary text-primary-foreground rounded-md text-sm font-medium hover:bg-primary/90 transition-colors"
                                    >
                                        Contribute
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </main>

                    {/* Table of Contents Sidebar (Right) */}
                    <aside className="w-full lg:w-80 flex-shrink-0">
                        <div className="sticky top-36">
                            <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
                                {/* Book Cover */}
                                <div className="mb-6 flex justify-center">
                                    <div className="w-40 rounded-lg overflow-hidden shadow-md">
                                        <img
                                            src="/imgs/books/Garlic-recipes-you-will-love.png"
                                            alt="Book Cover"
                                            className="w-full h-auto"
                                        />
                                    </div>
                                </div>
                                <h3 className="font-heading font-bold text-lg mb-4 flex items-center">
                                    <Icon name="ListBulletIcon" size={20} className="mr-2 text-primary" />
                                    Table of Contents
                                </h3>
                                <nav className="space-y-1">
                                    {chapters.map((chapter, index) => {
                                        if (chapter.type === 'header') {
                                            return (
                                                <div key={index} className="px-3 pt-4 pb-2 text-xs font-bold text-muted-foreground uppercase tracking-wider">
                                                    {chapter.title}
                                                </div>
                                            );
                                        }

                                        const isActive = pathname === chapter.path;
                                        return (
                                            <Link
                                                key={chapter.path}
                                                href={chapter.path!}
                                                className={`block px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive
                                                    ? 'bg-primary/10 text-primary'
                                                    : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                                                    }`}
                                            >
                                                {chapter.title}
                                            </Link>
                                        );
                                    })}
                                </nav>


                            </div>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}
